/* =============================================================
   HERO SECTION — Asymmetric split layout
   Left: Deep navy with headline + CTAs
   Right: Full-bleed photo of Perth bookkeeper
   Trust badges: Xero, BAS Agent, 5-star rating
   ============================================================= */

import { ArrowRight, Star, Shield, Award } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { HERO_IMAGES_OPTIMIZED, HERO_DIMENSIONS, HERO_IMAGE_FALLBACK } from "@/const/heroImagesOptimized";

function AnimatedCounter({ target, suffix = "" }: { target: number; suffix?: string }) {
  return <span>{target}{suffix}</span>;
}

export default function HeroSection() {
  // Do NOT animate hero section on load - it delays LCP
  // Hero text should render immediately for better mobile performance
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-[oklch(0.22_0.06_250)]">
      {/* Background subtle pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, oklch(0.72 0.12 75) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative w-full">
        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Left: Content */}
          <div className="flex flex-col justify-center px-6 lg:px-16 xl:px-24 py-20 lg:py-0">
            {/* Trust badge row */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5">
                <Shield size={12} className="text-[oklch(0.72_0.12_75)]" />
                <span className="text-white/90 text-xs font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Registered BAS Agent
                </span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5">
                <Award size={12} className="text-[oklch(0.72_0.12_75)]" />
                <span className="text-white/90 text-xs font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Xero Certified Advisor
                </span>
              </div>
              <div className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={10} className="text-[oklch(0.72_0.12_75)] fill-[oklch(0.72_0.12_75)]" />
                ))}
                <span className="text-white/90 text-xs font-medium ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  5-Star Rated
                </span>
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="block text-4xl lg:text-5xl xl:text-6xl font-700 mb-2">
                Perth Bookkeeper
              </span>
              <span className="block text-4xl lg:text-5xl xl:text-6xl font-700 text-[oklch(0.72_0.12_75)]">
                for Small Businesses
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-white/75 text-lg lg:text-xl mb-4 max-w-lg leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              Stop losing sleep over your books. We keep your financial records accurate, compliant, and up-to-date — so you can focus on growing your business.
            </p>

            {/* SEO keyword paragraph */}
            <p className="text-white/60 text-sm mb-8 max-w-lg leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              <strong className="text-white/80 font-medium">Bookkeeping Specialists Perth</strong> — Need help staying on top of your bookkeeping? The Perth Bookkeeper provides <strong className="text-white/80 font-medium">Xero bookkeeping</strong>, BAS preparation, payroll, bank reconciliations and clear reporting for Perth small businesses, tradies, sole traders and service-based businesses.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-10">
              <a href="#contact" className="btn-gold text-base">
                Book a Free Consultation
                <ArrowRight size={16} />
              </a>
              <a href="#pricing" className="btn-white-outline text-base">
                View Pricing
              </a>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/15">
              <div>
                <div className="stat-number text-2xl lg:text-3xl">
                  <AnimatedCounter target={150} suffix="+" />
                </div>
                <div className="text-white/60 text-xs mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Happy Clients
                </div>
              </div>
              <div>
                <div className="stat-number text-2xl lg:text-3xl">
                  <AnimatedCounter target={10} suffix="+" />
                </div>
                <div className="text-white/60 text-xs mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Years Experience
                </div>
              </div>
              <div>
                <div className="stat-number text-2xl lg:text-3xl">
                  <AnimatedCounter target={100} suffix="%" />
                </div>
                <div className="text-white/60 text-xs mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  ATO Compliant
                </div>
              </div>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative hidden lg:block">
            {/* Diagonal clip */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 0% 100%)' }}
            >
              <picture>
                {/* AVIF format (best compression) */}
                <source
                  srcSet={`
                    ${HERO_IMAGES_OPTIMIZED.mobile.avif} 480w,
                    ${HERO_IMAGES_OPTIMIZED.tablet.avif} 768w,
                    ${HERO_IMAGES_OPTIMIZED.desktop.avif} 1024w,
                    ${HERO_IMAGES_OPTIMIZED.large.avif} 1440w
                  `}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw"
                  type="image/avif"
                />
                {/* WebP format (good compression) */}
                <source
                  srcSet={`
                    ${HERO_IMAGES_OPTIMIZED.mobile.webp} 480w,
                    ${HERO_IMAGES_OPTIMIZED.tablet.webp} 768w,
                    ${HERO_IMAGES_OPTIMIZED.desktop.webp} 1024w,
                    ${HERO_IMAGES_OPTIMIZED.large.webp} 1440w
                  `}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw"
                  type="image/webp"
                />
                {/* JPEG fallback */}
                <img
                  src={HERO_IMAGE_FALLBACK}
                  srcSet={`
                    ${HERO_IMAGES_OPTIMIZED.mobile.jpeg} 480w,
                    ${HERO_IMAGES_OPTIMIZED.tablet.jpeg} 768w,
                    ${HERO_IMAGES_OPTIMIZED.desktop.jpeg} 1024w,
                    ${HERO_IMAGES_OPTIMIZED.large.jpeg} 1440w
                  `}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw"
                  alt="Professional Perth bookkeeper at her desk with Perth cityscape"
                  className="w-full h-full object-cover object-center"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width={HERO_DIMENSIONS[1440].width}
                  height={HERO_DIMENSIONS[1440].height}
                />
              </picture>
              {/* Overlay gradient for text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.22_0.06_250)/0.4] to-transparent" />
            </div>

            {/* Floating card: Free consultation */}
            <div className="absolute bottom-16 left-0 transform -translate-x-8 bg-white rounded-xl shadow-2xl p-5 w-64 z-10">
              <div className="mb-3">
                <div className="text-xs text-gray-500 mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>Latest Review</div>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={10} className="text-[oklch(0.72_0.12_75)] fill-[oklch(0.72_0.12_75)]" />
                  ))}
                </div>
              </div>
              <p className="text-gray-700 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                "Agnieszka is incredibly reliable and professional. Transitioning to Xero has been a game-changer."
              </p>
              <div className="text-gray-500 text-xs mt-2 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>— Perth Productions</div>
            </div>
          </div>
        </div>

        {/* Mobile image strip */}
        <div className="lg:hidden absolute bottom-0 left-0 right-0 h-48 overflow-hidden opacity-20">
          <picture>
            <source srcSet={HERO_IMAGES_OPTIMIZED.mobile.avif} type="image/avif" />
            <source srcSet={HERO_IMAGES_OPTIMIZED.mobile.webp} type="image/webp" />
            <img
              src={HERO_IMAGES_OPTIMIZED.mobile.jpeg}
              alt="The Perth Bookkeeper - Professional bookkeeping services"
              className="w-full h-full object-cover object-top"
              loading="lazy"
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.22_0.06_250)] to-transparent" />
        </div>
      </div>
    </section>
  );
}
